import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ChevronRight, Check, Minus,
  Layers, GitBranch, Ruler, MapPin, FileText, Camera,
  Hammer, Activity, WifiOff, ClipboardList, ShieldCheck, HardHat,
  Plane, Construction, Droplets, TrafficCone, Building2, Route,
} from "lucide-react";
import RequestDemoModal from "@/components/marketing/RequestDemoModal";

const LOGO = "/brand/forgedops-plans-logo.png";
const MARK = "/brand/forgedops-plans-mark.png";

// Real product screenshots provided by the operator. Hosted on the asset CDN
// the platform already uses. These are PRODUCT mockups built in-house — not
// stock photography — so they're safe to surface publicly.
const HERO_SHOT     = "/marketing/plans-hero.jpg";
const SHOT_AIRFIELD = "/marketing/plans-airfield.jpg";
const SHOT_DRAINAGE = "/marketing/plans-drainage-sheet.jpg";
const SHOT_FIELD    = "/marketing/plans-field-mode.jpg";
const SHOT_MARKUP   = "/marketing/plans-markup.jpg";
const SHOT_REVISION = "/marketing/plans-revision.jpg";
const SHOT_MEASURE  = "/marketing/plans-measure.jpg";
const SHOT_DETAIL   = "/marketing/plans-detail.jpg";

/**
 * MarketingHome — Public ForgedOps Plans homepage at `/`.
 *
 * Dark, premium, orange-accent, heavy-civil aesthetic inspired by
 * forgedopshq.com. Contains no customer data. Contains no auth-gated
 * routes. Public users see only marketing copy + Demo CTA + Customer Login.
 *
 * Subtle "ForgedOps Staff" link routes to /platform (which protected-routes
 * back through /login for platform-admin email allowlist auth).
 */
export default function MarketingHome() {
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    document.title = "ForgedOps Plans | Construction Plan Intelligence Platform";
  }, []);

  const openDemo = () => setDemoOpen(true);

  return (
    <div className="min-h-screen bg-[var(--fop-bg)] text-[var(--fop-text)]" data-testid="marketing-home">
      <TopNav onRequestDemo={openDemo} />

      <Hero onRequestDemo={openDemo} />
      <PlanRoomShowcase />
      <BuiltForBar />
      <BuiltByBuilders />
      <IndustryCards />
      <Problem />
      <Difference />
      <Comparison />
      <FieldWorkflow />
      <PricingCTA onRequestDemo={openDemo} />
      <FinalCTA onRequestDemo={openDemo} />
      <Footer />

      <RequestDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}

/* =================================================================
 * Top navigation
 * =============================================================== */
function TopNav({ onRequestDemo }) {
  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-md"
      style={{
        background: "rgba(2, 6, 23, 0.78)",
        borderBottom: "1px solid var(--fop-border)",
      }}
      data-testid="marketing-nav"
    >
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img src={LOGO} alt="ForgedOps Plans" className="h-7 w-auto select-none" draggable={false} />
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm text-[var(--fop-text-2)]">
          <a href="#plan-room" className="hover:text-[var(--fop-text)] transition-colors">Platform</a>
          <a href="#industries" className="hover:text-[var(--fop-text)] transition-colors">Industries</a>
          <a href="#workflow" className="hover:text-[var(--fop-text)] transition-colors">Workflow</a>
          <a href="#pricing" className="hover:text-[var(--fop-text)] transition-colors" data-testid="nav-pricing">Pricing</a>
          <a href="#compare" className="hover:text-[var(--fop-text)] transition-colors">Compare</a>
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/login"
            className="btn btn-ghost hidden sm:inline-flex"
            style={{ minHeight: "40px", padding: "0 14px", fontSize: "14px" }}
            data-testid="nav-login"
          >
            Customer Login
          </Link>
          <button
            onClick={onRequestDemo}
            className="btn btn-ghost hidden lg:inline-flex"
            style={{ minHeight: "40px", padding: "0 14px", fontSize: "14px" }}
            data-testid="nav-request-demo"
          >
            Request Demo
          </button>
          <Link
            to="/signup"
            className="btn btn-primary"
            style={{ minHeight: "40px", padding: "0 16px", fontSize: "14px" }}
            data-testid="nav-start-trial"
          >
            Start Free Trial
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </header>
  );
}

/* =================================================================
 * Hero
 * =============================================================== */
function Hero({ onRequestDemo }) {
  return (
    <section className="relative overflow-hidden" id="top">
      {/* Tactical grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.18,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at 70% 30%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at 70% 30%, black 30%, transparent 75%)",
        }}
      />
      {/* Orange ember glow */}
      <div
        className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(249,115,22,0.18), transparent 70%)" }}
      />
      {/* Amber accent */}
      <div
        className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(245,158,11,0.10), transparent 70%)" }}
      />
      {/* Industrial stripe at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1.5"
        style={{
          background:
            "repeating-linear-gradient(135deg, var(--fop-primary) 0 14px, transparent 14px 28px)",
          opacity: 0.55,
        }}
      />

      <div className="relative max-w-[1320px] mx-auto px-5 sm:px-8 pt-14 sm:pt-20 lg:pt-24 pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-10 lg:gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full font-mono text-[11px] uppercase tracking-[0.28em]"
              style={{
                background: "rgba(249, 115, 22, 0.10)",
                border: "1px solid rgba(249, 115, 22, 0.35)",
                color: "var(--fop-primary)",
              }}
              data-testid="hero-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--fop-primary)]" />
              Heavy Civil · DOT · FAA · Utilities
            </div>

            <h1 className="font-display mt-7 leading-[0.98] tracking-tight"
                style={{ fontSize: "clamp(40px, 6.4vw, 72px)" }}>
              <span className="block text-[var(--fop-text)]">Build better.</span>
              <span className="block text-[var(--fop-text)]">Build together.</span>
              <span className="block mt-3 text-[var(--fop-primary)] font-display"
                    style={{ fontSize: "clamp(18px, 2.2vw, 26px)", fontWeight: 600 }}>
                ForgedOps Plans<sup className="text-[0.55em] align-super ml-0.5">™</sup> — Construction Plan Intelligence Platform
              </span>
            </h1>

            <p className="mt-6 sm:mt-8 text-[var(--fop-text-2)] max-w-2xl"
               style={{ fontSize: "clamp(16px, 1.35vw, 19px)", lineHeight: 1.55 }}>
              From plan review to field execution, ForgedOps Plans gives heavy civil
              teams one operational source of truth for drawings, revisions,
              measurements, documents, field notes, utility coordination, and
              project intelligence.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/signup" className="btn btn-primary"
                    style={{ minHeight: "52px", padding: "0 26px", fontSize: "16px" }}
                    data-testid="hero-start-trial">
                Start Your 14-Day Free Trial
                <ArrowRight size={18} />
              </Link>
              <button onClick={onRequestDemo} className="btn btn-ghost"
                      style={{ minHeight: "52px", padding: "0 26px", fontSize: "16px" }}
                      data-testid="hero-request-demo">
                Request Demo
              </button>
            </div>
            <div className="mt-3 text-sm text-[var(--fop-text-3)]" data-testid="hero-trial-disclosure">
              Card required. Your selected plan starts billing after 14 days unless you cancel.
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-4 max-w-2xl">
              <Stat label="Sheet revisions" value="Tracked" />
              <Stat label="Field acks" value="Required" />
              <Stat label="GPS / stationing" value="Live" />
              <Stat label="Offline mode" value="Ready" />
            </div>
          </div>

          {/* Product mockup */}
          <div className="relative w-full">
            <div
              className="absolute -inset-6 rounded-2xl pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at 30% 10%, rgba(249,115,22,0.18), transparent 65%)",
                filter: "blur(20px)",
              }}
            />
            <div
              className="relative rounded-xl overflow-hidden"
              style={{
                border: "1px solid var(--fop-border-2)",
                boxShadow:
                  "0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(249,115,22,0.10) inset",
              }}
              data-testid="hero-product-shot"
            >
              <img src={HERO_SHOT} alt="ForgedOps Plans — operational plan room with utility conflicts, layers, measurements" loading="eager" className="block w-full h-auto" />
            </div>
            <div
              className="absolute -bottom-3 left-6 right-6 h-3 rounded-b-xl pointer-events-none"
              style={{ background: "rgba(0,0,0,0.4)", filter: "blur(8px)" }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-l-2 pl-3"
         style={{ borderColor: "var(--fop-primary)" }}>
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--fop-text-3)]">
        {label}
      </div>
      <div className="text-[var(--fop-text)] font-display text-lg mt-0.5">{value}</div>
    </div>
  );
}

/* =================================================================
 * Built For (industries strip)
 * =============================================================== */
function BuiltForBar() {
  const items = [
    "Heavy Civil",
    "DOT / Highway",
    "Airports / FAA",
    "Utilities",
    "Site Development",
    "Drainage",
    "Municipal Infrastructure",
    "Industrial Construction",
  ];
  return (
    <section id="built-for"
             className="border-y"
             style={{ borderColor: "var(--fop-border)", background: "rgba(17, 24, 39, 0.55)" }}>
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-8 sm:py-10">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--fop-text-3)] mb-5">
          Who it&apos;s built for
        </div>
        <div className="flex flex-wrap gap-x-7 gap-y-3">
          {items.map((label) => (
            <div key={label}
                 className="flex items-center gap-2 text-[var(--fop-text-2)]">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--fop-primary)]" />
              <span className="text-sm sm:text-base">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =================================================================
 * Problem
 * =============================================================== */
function Problem() {
  const pains = [
    "Paper sheets that go missing on site",
    "Shared drives nobody trusts",
    "Bluebeam files lost in email threads",
    "Outdated revisions still being built",
    "Field notes that never make it back to the office",
  ];
  const consequences = ["Rework", "Missed revisions", "Field confusion", "Utility conflicts", "Production delays"];
  return (
    <section className="relative">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <SectionLabel>The problem</SectionLabel>
            <h2 className="font-display mt-3"
                style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1.1 }}>
              Most heavy civil teams still manage plans the same way they did in 2005.
            </h2>
            <p className="mt-5 text-[var(--fop-text-2)] max-w-xl">
              Plan rooms are scattered. Revisions don&apos;t reach the field.
              Measurements live on paper. Utility conflicts are caught the
              day they&apos;re hit. The cost shows up in rework, missed pours,
              and angry foremen at 6 AM.
            </p>
          </div>

          <div className="space-y-3">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-2">
              What that looks like in the field
            </div>
            {pains.map((p) => (
              <div key={p}
                   className="flex items-start gap-3 px-4 py-3 rounded-md"
                   style={{
                     background: "var(--fop-surface)",
                     border: "1px solid var(--fop-border)",
                   }}>
                <Minus size={16} className="text-[var(--fop-danger)] mt-1 shrink-0" />
                <div className="text-[var(--fop-text-2)] text-sm sm:text-base">{p}</div>
              </div>
            ))}

            <div className="pt-6">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-3">
                The cost
              </div>
              <div className="flex flex-wrap gap-2">
                {consequences.map((c) => (
                  <span key={c}
                        className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded"
                        style={{
                          background: "rgba(220, 38, 38, 0.08)",
                          border: "1px solid rgba(220, 38, 38, 0.35)",
                          color: "#FCA5A5",
                        }}>
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =================================================================
 * The ForgedOps Difference
 * =============================================================== */
function Difference() {
  const capabilities = [
    { icon: Layers,        title: "Plan Room",           desc: "Every plan set, every project, one operational room." },
    { icon: FileText,      title: "Sheet Viewer",        desc: "iPad-first deep-zoom viewer. Sun-readable, retina-crisp." },
    { icon: GitBranch,     title: "Revision Tracking",   desc: "Every sheet diffed. Nothing slips through to the field." },
    { icon: ShieldCheck,   title: "Field Acknowledgments", desc: "Crews confirm receipt of the current revision. Audit-grade." },
    { icon: Ruler,         title: "Measurements",        desc: "Calibrated scale, takeoffs, area, length, station offsets." },
    { icon: MapPin,        title: "GPS & Stationing",    desc: "Stand on the alignment, see the sheet, drop the pin." },
    { icon: Hammer,        title: "Structures & Pipe Intelligence", desc: "Pipe runs, structures, utilities — read straight off plans." },
    { icon: ClipboardList, title: "Shop Drawings & Docs", desc: "Submittals, specs, RFIs — linked to the objects they govern." },
    { icon: Camera,        title: "Photos & Notes",      desc: "Tied to the sheet, the object, and the GPS pin." },
    { icon: Activity,      title: "Assignments & Object Health", desc: "What needs eyes today. What got fixed yesterday." },
    { icon: WifiOff,       title: "Offline-Ready Field Access", desc: "Drawings, photos, measurements — when signal drops." },
  ];
  return (
    <section className="relative"
             style={{
               background:
                 "linear-gradient(180deg, var(--fop-bg) 0%, rgba(17, 24, 39, 0.7) 100%)",
               borderTop: "1px solid var(--fop-border)",
             }}>
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-2xl">
          <SectionLabel>The ForgedOps difference</SectionLabel>
          <h2 className="font-display mt-3"
              style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1.08 }}>
            One operational surface. Plan to field. Field to closeout.
          </h2>
          <p className="mt-5 text-[var(--fop-text-2)]">
            ForgedOps Plans isn&apos;t a viewer with a comment thread bolted on.
            It&apos;s a construction-native operating system for the plan
            room and the people working off it.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {capabilities.map(({ icon: Icon, title, desc }) => (
            <div key={title}
                 className="fop-card p-5 sm:p-6 transition-transform hover:-translate-y-0.5"
                 data-testid={`cap-${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 rounded-md inline-flex items-center justify-center"
                     style={{
                       background: "rgba(249, 115, 22, 0.10)",
                       border: "1px solid rgba(249, 115, 22, 0.35)",
                     }}>
                  <Icon size={18} className="text-[var(--fop-primary)]" />
                </div>
                <div className="font-display text-lg text-[var(--fop-text)]">{title}</div>
              </div>
              <p className="text-sm text-[var(--fop-text-2)] leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =================================================================
 * Comparison Table
 * =============================================================== */
function Comparison() {
  // Wave 11 (2026-09-11) — claim/marketing truth audit. Prior rows
  // marked "hcss: false" for GPS positioning, offline field access,
  // structure/pipe intelligence, and shop-drawing linking were checked
  // against HCSS's own published product pages (hcss.com/products/
  // field-blueprint-app, drawing-management-software, construction-
  // plan-management, construction-mapping-software — accessed
  // 2026-09-11) and found to be FALSE or materially overstated: HCSS
  // Plans genuinely has GPS-tagged photos/location services, a robust
  // offline mode, "Utility Intelligence" location-based conflict
  // checking, and shop-drawing organization + bid-item linking.
  // Corrected below rather than left as fabricated competitor claims.
  // The one row kept as a real gap ("field acknowledgment of
  // revisions" — an explicit sign-off GATE distinct from HCSS's
  // automatic slip-sheet/watermark push) is based on the same
  // research finding no such gated-acknowledgment workflow in HCSS's
  // published feature set. The former "single operational source of
  // truth" row was removed — too subjective to verify either way.
  const rows = [
    { feature: "Built for construction",            sd: false, bb: false, hcss: true,  fop: true },
    { feature: "Plan revisions tracked automatically", sd: false, bb: false, hcss: true,  fop: true },
    { feature: "Field acknowledgment of revisions", sd: false, bb: false, hcss: false, fop: true },
    { feature: "GPS positioning on plans",          sd: false, bb: false, hcss: true,  fop: true },
    { feature: "Structure / pipe intelligence",     sd: false, bb: false, hcss: true,  fop: true },
    { feature: "Shop drawings linked to objects",   sd: false, bb: false, hcss: true,  fop: true },
    { feature: "Offline-ready field access",        sd: false, bb: false, hcss: true,  fop: true },
  ];
  const cols = [
    { key: "sd",   label: "Shared Drives" },
    { key: "bb",   label: "Bluebeam" },
    { key: "hcss", label: "HCSS Plans" },
    { key: "fop",  label: "ForgedOps Plans", highlight: true },
  ];
  return (
    <section id="compare" className="relative">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-2xl">
          <SectionLabel>How it compares</SectionLabel>
          <h2 className="font-display mt-3"
              style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1.08 }}>
            More construction-focused. More operationally intelligent.
          </h2>
        </div>

        <div className="mt-10 overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[640px]">
            <thead>
              <tr>
                <th className="py-4 pr-4 text-xs font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] w-[42%]">
                  Capability
                </th>
                {cols.map((c) => (
                  <th key={c.key}
                      className={`py-4 px-3 text-xs font-mono uppercase tracking-[0.18em] text-center
                                  ${c.highlight ? "text-[var(--fop-primary)]" : "text-[var(--fop-text-3)]"}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.feature}
                    className="border-t"
                    style={{ borderColor: "var(--fop-border)" }}>
                  <td className="py-4 pr-4 text-[var(--fop-text-2)] text-sm sm:text-base">{r.feature}</td>
                  {cols.map((c) => (
                    <td key={c.key}
                        className={`py-4 px-3 text-center
                                    ${c.highlight ? "bg-[rgba(249,115,22,0.04)]" : ""}`}>
                      {r[c.key] ? (
                        <Check size={18}
                               className={`inline-block ${c.highlight ? "text-[var(--fop-primary)]" : "text-[var(--fop-success)]"}`} />
                      ) : (
                        <span className="text-[var(--fop-text-4)]">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-[var(--fop-text-4)]">
          Comparison reflects each vendor's own published product pages as of Sep 2026, not independent hands-on testing. Trademarks belong to their respective owners.
        </p>
      </div>
    </section>
  );
}

/* =================================================================
 * Field-First Workflow
 * =============================================================== */
function FieldWorkflow() {
  const steps = ["Plan", "Object", "Document", "Photo", "Note", "Assignment", "Revision", "Closeout"];
  return (
    <section id="workflow"
             className="relative"
             style={{
               borderTop: "1px solid var(--fop-border)",
               background: "rgba(17, 24, 39, 0.55)",
             }}>
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-2xl">
          <SectionLabel>Field-first workflow</SectionLabel>
          <h2 className="font-display mt-3"
              style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1.08 }}>
            A single thread from plan to closeout.
          </h2>
          <p className="mt-5 text-[var(--fop-text-2)]">
            Every object — a structure, a manhole, a pipe run, a foundation — carries
            its own thread of documents, photos, notes, assignments, and revisions.
            Nothing disconnected. Nothing orphaned.
          </p>
        </div>

        <div className="mt-12 -mx-2 overflow-x-auto pb-2">
          <div className="flex items-center gap-2 min-w-max px-2">
            {steps.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex items-center gap-2.5 px-4 py-3 rounded-md"
                     style={{
                       background: "var(--fop-surface-2)",
                       border: "1px solid var(--fop-border-2)",
                     }}>
                  <span className="font-mono text-[10px] text-[var(--fop-primary)]">0{i + 1}</span>
                  <span className="font-display text-[var(--fop-text)] text-sm sm:text-base">{s}</span>
                </div>
                {i < steps.length - 1 && (
                  <ChevronRight size={18} className="text-[var(--fop-text-4)] shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* =================================================================
 * Pricing CTA
 * =============================================================== */
function PricingCTA({ onRequestDemo }) {
  const cards = [
    { title: "Schedule Demo",        desc: "Live walkthrough with a ForgedOps operator.", cta: "Schedule Demo" },
    { title: "Enterprise Deployment", desc: "Multi-tenant, audit-grade, dedicated environments. Custom limits, negotiated.", cta: "Talk to Us" },
    { title: "Talk to ForgedOps",     desc: "Operations, onboarding, and rollout questions.", cta: "Get in Touch" },
  ];
  return (
    <section id="pricing" className="relative">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-2xl">
          <SectionLabel>Pricing</SectionLabel>
          <h2 className="font-display mt-3"
              style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1.08 }}>
            Start free. No sales call required.
          </h2>
          <p className="mt-5 text-[var(--fop-text-2)]">
            ForgedOps Plans Starter is self-service — pick monthly or annual, start
            your 14-day free trial, and you're in. Need more capacity? Enterprise
            deployments are priced to your operation — projects, plan volume, field
            user count, and integrations.
          </p>
        </div>

        {/* Founder Commercial Amendment (2026-09-07) — Starter is the one
            tier a small crew can buy right now, no sales call needed. */}
        <div className="mt-10">
          <Link
            to="/signup"
            data-testid="pricing-cta-starter-signup"
            className="fop-card block p-6 sm:p-7 hover:-translate-y-0.5 transition-transform"
            style={{ borderColor: "var(--fop-primary)" }}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-primary)] mb-1.5">
                  14-day free trial — card required, no sales call
                </div>
                <div className="font-display text-xl text-[var(--fop-text)]">ForgedOps Plans Starter</div>
                <div className="text-sm text-[var(--fop-text-2)] mt-1.5 max-w-lg leading-relaxed">
                  Built for smaller crews that need serious plan tools without enterprise-scale overhead.
                  5 users · 3 active projects · 150 active plan pages · 15 GiB storage (~16.1 GB) · 100 Ask queries/mo.
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display text-2xl text-[var(--fop-text)]">$129<span className="text-sm text-[var(--fop-text-3)] font-sans">/mo</span></div>
                <div className="text-xs text-[var(--fop-text-3)] mt-0.5">or $1,290/yr</div>
                <div className="text-[11px] text-[var(--fop-text-4)] mt-1">$0 due today · bills after 14 days unless canceled</div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-mono uppercase tracking-wider text-[var(--fop-primary)]">
                  Start Free Trial <ArrowRight size={14} />
                </div>
              </div>
            </div>
          </Link>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {cards.map((c) => (
            <button
              key={c.title}
              onClick={onRequestDemo}
              data-testid={`pricing-cta-${c.title.replace(/\s+/g, "-").toLowerCase()}`}
              className="fop-card text-left p-6 hover:-translate-y-0.5 transition-transform"
              style={{ minHeight: "180px" }}
            >
              <div className="font-display text-lg text-[var(--fop-text)] mb-2">{c.title}</div>
              <div className="text-sm text-[var(--fop-text-2)] mb-5 leading-relaxed">{c.desc}</div>
              <div className="flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-[var(--fop-primary)]">
                {c.cta} <ArrowRight size={14} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =================================================================
 * Final CTA
 * =============================================================== */
function FinalCTA({ onRequestDemo }) {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #0a0f1c 0%, #111827 60%, #0a0f1c 100%)",
        borderTop: "1px solid var(--fop-border)",
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.4,
          background:
            "radial-gradient(circle at 20% 50%, rgba(249,115,22,0.18), transparent 50%), radial-gradient(circle at 80% 50%, rgba(245,158,11,0.12), transparent 55%)",
        }}
      />
      <div className="absolute bottom-0 left-0 right-0 h-1.5"
        style={{
          background:
            "repeating-linear-gradient(135deg, var(--fop-primary) 0 14px, transparent 14px 28px)",
          opacity: 0.55,
        }}
      />
      <div className="relative max-w-[1100px] mx-auto px-5 sm:px-8 py-20 sm:py-28 text-center">
        <SectionLabel center>Ready when you are</SectionLabel>
        <h2 className="font-display mt-3 mx-auto max-w-3xl"
            style={{ fontSize: "clamp(30px, 4.6vw, 56px)", lineHeight: 1.04 }}>
          Start your 14-day free trial.
        </h2>
        <p className="mt-5 text-[var(--fop-text-2)] max-w-xl mx-auto">
          Card required. Your selected plan starts billing after 14 days unless you
          cancel. Prefer a walkthrough first? Schedule a demo with a ForgedOps operator.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/signup" className="btn btn-primary"
                style={{ minHeight: "52px", padding: "0 30px", fontSize: "16px" }}
                data-testid="final-start-trial">
            Start Free Trial
            <ArrowRight size={18} />
          </Link>
          <button onClick={onRequestDemo} className="btn btn-ghost"
                  style={{ minHeight: "52px", padding: "0 30px", fontSize: "16px" }}
                  data-testid="final-request-demo">
            Request Demo
          </button>
          <Link to="/login" className="btn btn-ghost"
                style={{ minHeight: "52px", padding: "0 30px", fontSize: "16px" }}
                data-testid="final-customer-login">
            Customer Login
          </Link>
        </div>
      </div>
    </section>
  );
}

/* =================================================================
 * Footer
 * =============================================================== */
function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="border-t"
      style={{ borderColor: "var(--fop-border)", background: "var(--fop-bg)" }}
      data-testid="marketing-footer"
    >
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-10">
          <div className="max-w-sm">
            <img src={MARK} alt="ForgedOps" className="h-8 w-auto mb-4 select-none" draggable={false} />
            <div className="font-display text-[var(--fop-text)] text-lg mb-2">ForgedOps Plans™</div>
            <p className="text-sm text-[var(--fop-text-2)] leading-relaxed">
              Construction Plan Intelligence Platform.
              Built for heavy civil, DOT, FAA, utility, and infrastructure contractors.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-10 gap-y-6 text-sm">
            <FooterCol title="Platform">
              <a href="#plan-room" className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]">Overview</a>
              <a href="#industries" className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]">Industries</a>
              <a href="#workflow" className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]">Workflow</a>
              <a href="#compare"  className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]">Compare</a>
            </FooterCol>
            <FooterCol title="Legal">
              <Link to="/legal/terms" className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]" data-testid="footer-terms-link">
                Terms of Use
              </Link>
              <Link to="/legal/privacy" className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>
              <Link to="/legal/security" className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]" data-testid="footer-security-link">
                Security
              </Link>
              <Link to="/trust" className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]" data-testid="footer-trust-link">
                Trust Center
              </Link>
              <Link to="/support" className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]" data-testid="footer-support-link">
                Support
              </Link>
              <Link to="/contact" className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]" data-testid="footer-contact-link">
                Contact
              </Link>
            </FooterCol>
            <FooterCol title="Access">
              <Link to="/login" className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]" data-testid="footer-login">
                Customer Login
              </Link>
              <a href="mailto:info@forgedopshq.com"
                 className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]">
                Contact
              </a>
            </FooterCol>
            <FooterCol title="Company">
              <a href="https://forgedopshq.com" target="_blank" rel="noreferrer noopener"
                 className="block text-[var(--fop-text-2)] hover:text-[var(--fop-text)]">
                ForgedOps HQ
              </a>
            </FooterCol>
          </div>
        </div>

        <div
          className="mt-10 pt-6 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs font-mono uppercase tracking-[0.2em] text-[var(--fop-text-4)]"
          style={{ borderColor: "var(--fop-border)" }}
        >
          <div>© {year} ForgedOps LLC. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <span>FORGEDOPS PLANS™</span>
            <span className="opacity-30">·</span>
            <Link
              to="/platform"
              className="inline-flex items-center gap-1.5 text-[var(--fop-text-4)] hover:text-[var(--fop-primary)] transition-colors"
              title="ForgedOps staff sign-in"
              data-testid="footer-ops-console"
            >
              <HardHat size={11} />
              ForgedOps Staff
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)] mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

/* =================================================================
 * Small bits
 * =============================================================== */
function SectionLabel({ children, center }) {
  return (
    <div className={`font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--fop-primary)] ${center ? "text-center" : ""}`}>
      {children}
    </div>
  );
}


/* =================================================================
 * Plan Room Showcase — "Built around the plan sheet"
 * =============================================================== */
function PlanRoomShowcase() {
  const shots = [
    { src: SHOT_FIELD,    label: "Field Mode · Utility Crossings", desc: "GPS-aware utility coordination on the latest plan revision." },
    { src: SHOT_AIRFIELD, label: "Airfield · Structures & Conflicts", desc: "Storm structures, edge lights, pavement removal limits." },
    { src: SHOT_REVISION, label: "Revision Review", desc: "Added · Modified · Removed — every sheet diffed, every change owned." },
    { src: SHOT_MEASURE,  label: "Measurements & Stationing", desc: "Calibrated takeoffs with area, perimeter, and station offsets." },
    { src: SHOT_MARKUP,   label: "Field Markup", desc: "Highlight, callout, and annotate without leaving the sheet." },
    { src: SHOT_DETAIL,   label: "Detail View & Object Card", desc: "Drainage structure DS-04 with inverts, material, sump depth." },
  ];
  return (
    <section
      id="plan-room"
      className="relative"
      style={{
        background: "linear-gradient(180deg, var(--fop-bg) 0%, rgba(17,24,39,0.7) 100%)",
        borderTop: "1px solid var(--fop-border)",
      }}
    >
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-2xl">
          <SectionLabel>Built around the plan sheet</SectionLabel>
          <h2 className="font-display mt-3"
              style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1.08 }}>
            Not a file cabinet. An operational plan room.
          </h2>
          <p className="mt-5 text-[var(--fop-text-2)]">
            Every sheet is a live surface — measurements, structures, utilities,
            photos, notes, revisions, and field acknowledgments all live on the
            drawing where they belong.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {shots.map((s) => (
            <ProductShot key={s.label} {...s} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductShot({ src, label, desc }) {
  return (
    <div
      className="group fop-card overflow-hidden p-0"
      data-testid={`shot-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
    >
      <div className="relative w-full overflow-hidden"
           style={{ aspectRatio: "1 / 1", background: "#0a0f1c" }}>
        <img src={src} alt={label} loading="lazy"
             className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
        <div className="absolute inset-x-0 bottom-0 h-20"
             style={{ background: "linear-gradient(180deg, transparent, rgba(2,6,23,0.85))" }} />
      </div>
      <div className="px-5 py-4">
        <div className="font-display text-[var(--fop-text)] text-base">{label}</div>
        <div className="text-sm text-[var(--fop-text-2)] mt-1 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

/* =================================================================
 * Built by Builders, Not Software Tourists
 * =============================================================== */
function BuiltByBuilders() {
  return (
    <section className="relative"
             style={{ background: "var(--fop-bg)", borderTop: "1px solid var(--fop-border)" }}>
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid lg:grid-cols-[1fr_minmax(0,1.1fr)] gap-12 lg:gap-16 items-center">
          <div>
            <SectionLabel>Built by builders, not software tourists</SectionLabel>
            <h2 className="font-display mt-3"
                style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1.08 }}>
              Forged in heavy civil. Not in a startup conference room.
            </h2>
            <p className="mt-5 text-[var(--fop-text-2)]">
              ForgedOps Plans was built from real heavy civil field experience —
              DOT jobs, airport work, utility conflicts, earthwork, drainage,
              paving, revisions, RFIs, and crews trying to build from the latest
              information without losing time.
            </p>
            <div className="mt-7 grid sm:grid-cols-2 gap-2.5">
              {[
                "Designed by superintendents and foremen, not product marketers",
                "iPad-first — sun-readable in 90° daylight",
                "Handles real plan sets: 200, 600, 1,200+ sheets",
                "Survives a dead signal in the right-of-way",
                "Speaks construction: stations, inverts, RIM elevations",
                "Built for crews moving on production pressure",
              ].map((t) => (
                <div key={t} className="flex items-start gap-2.5 text-sm text-[var(--fop-text-2)]">
                  <Check size={16} className="text-[var(--fop-primary)] mt-0.5 shrink-0" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div
              className="absolute -inset-4 rounded-2xl pointer-events-none"
              style={{
                background: "radial-gradient(ellipse at 70% 30%, rgba(245,158,11,0.14), transparent 70%)",
                filter: "blur(20px)",
              }}
            />
            <div className="relative rounded-xl overflow-hidden"
                 style={{
                   border: "1px solid var(--fop-border-2)",
                   boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
                 }}>
              <img src={SHOT_FIELD} alt="ForgedOps Plans field mode" loading="lazy" className="block w-full h-auto" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =================================================================
 * Industry-specific cards
 * =============================================================== */
function IndustryCards() {
  const cards = [
    { icon: Construction,  title: "Heavy Civil",                desc: "Subs, supers, foremen, and crews all on the same revision — across earthwork, structures, drainage, and paving." },
    { icon: Route,         title: "DOT / Highway",              desc: "Stationing-aware drawings, alignment cross-sections, lane-staging revisions, and field markup tied to mileposts." },
    { icon: Plane,         title: "FAA / Airports",             desc: "Airside work zones, RSA boundaries, runway closure phasing, edge-light layouts, and FAA submittal traceability." },
    { icon: Hammer,        title: "Utilities",                  desc: "Water · sewer · gas · electric — color-coded crossings, depth conflicts, and clearance call-outs on a live sheet." },
    { icon: Droplets,      title: "Drainage",                   desc: "Storm structures, RIM/INV elevations, slope, and pipe runs read straight off the plans, ready for the field." },
    { icon: Layers,        title: "Site Development",           desc: "Phased grading, erosion control, building pads, and utility tie-ins coordinated across every trade." },
    { icon: TrafficCone,   title: "Paving",                     desc: "Lift schedules, base prep limits, joints, and tack-coat sequencing tracked sheet-by-sheet." },
    { icon: Building2,     title: "Municipal Infrastructure",   desc: "Roadways, water mains, lift stations, and public-agency submittal sets organized for audit-ready closeout." },
  ];
  return (
    <section
      id="industries"
      className="relative"
      style={{ background: "rgba(17,24,39,0.45)", borderTop: "1px solid var(--fop-border)" }}
    >
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-2xl">
          <SectionLabel>Built for your work</SectionLabel>
          <h2 className="font-display mt-3"
              style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1.08 }}>
            One platform. Every kind of heavy civil job.
          </h2>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {cards.map(({ icon: Icon, title, desc }) => (
            <div key={title}
                 data-testid={`industry-${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                 className="fop-card p-5 transition-transform hover:-translate-y-0.5"
                 style={{ minHeight: "188px" }}>
              <div className="w-9 h-9 rounded-md inline-flex items-center justify-center mb-3"
                   style={{
                     background: "rgba(249, 115, 22, 0.10)",
                     border: "1px solid rgba(249, 115, 22, 0.35)",
                   }}>
                <Icon size={18} className="text-[var(--fop-primary)]" />
              </div>
              <div className="font-display text-[var(--fop-text)] text-base mb-1.5">{title}</div>
              <div className="text-sm text-[var(--fop-text-2)] leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
